import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../../api.js';
import { t } from '../../i18n/index.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { CopyField } from '../../components/CopyField.js';
import { PUBLIC_ORIGIN } from '../../joinLinks.js';
import {
  BINGO_BATCH_MAX,
  BINGO_LABEL_MAX,
  formatPhone,
  normalizeEmail,
  normalizePhone,
  signupMessage,
  type BingoMailConfig,
  type BingoSignupConfig,
  type BingoSignupView,
  type BingoSmsConfig,
} from '../../../shared/bingo.js';

// Codes by text message for the host (user decisions 2026-10-01): the sign-up form (open/closed,
// maximum, info, message template), the sign-ups (select → batch name → send codes; WhatsApp as a
// fallback), the phone (SMS Gateway for Android: address, user, password, test message) and the
// mailbox for codes by e-mail (instant batch, subject, template, SMTP settings, test mail). Sign-up by
// text message "BINGO 3 Nickname" (user decisions 2026-10-07): a switch of its own, saved at once.

type Flash = { ok: boolean; text: string } | null;
interface View {
  signups: BingoSignupView[];
  job: { total: number; done: number } | null;
  config: BingoSignupConfig & { botCheck: 'ok' | 'test' | 'missing' };
  sms: BingoSmsConfig;
  mail: BingoMailConfig;
  /** The administrator may change the phone and the mailbox; a bingo host may not (2026-10-03). */
  connections: boolean;
  batches: { id: number; label: string; free: number }[];
  /** The phone's inbox was last read (sign-up by text message). */
  smsInbox: { okAt: number | null; error: string | null };
  /** The general coupon limit per player (set here since 2026-10-07). */
  playerMax: number;
}
interface Plan {
  needed: number;
  messages: number;
  sms: number;
  emails: number;
  batch: { exists: boolean; free: number; registrationOpen: boolean };
  creates: number[];
}
const s = () => t.admin.sms;
const errorText = (err: unknown) => t.errors[err instanceof ApiError ? err.code : 'network'];
const when = (ms: number) => new Date(ms).toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' });
const spaced = (code: string) => `${code.slice(0, 4)} ${code.slice(4)}`;
/** The message, as the server writes it (for the WhatsApp fallback); no links (2026-10-02). */
const messageText = signupMessage;

export function SignupsAdmin(props: { onFlash: (f: Flash) => void }) {
  const [view, setView] = useState<View | null>(null);
  const load = () =>
    api<View>('GET', '/api/admin/bingo/signups')
      .then(setView)
      .catch((err) => props.onFlash({ ok: false, text: errorText(err) }));
  useEffect(() => {
    void load();
  }, []);
  // While sending, the list follows the progress (and the delivery states shortly after).
  useEffect(() => {
    const busy = view?.job || view?.signups.some((x) => x.status === 'sending' || x.status === 'sent');
    if (!busy) return;
    const timer = setTimeout(() => void load(), view?.job ? 1500 : 10_000);
    return () => clearTimeout(timer);
  }, [view]);
  if (!view) return <p class="muted">{t.admin.autoBingo.loading}</p>;
  return (
    <>
      <FormSettings view={view} onView={setView} onFlash={props.onFlash} />
      <SignupList view={view} onView={setView} onFlash={props.onFlash} />
      {/* The phone and the mailbox (addresses, user names, passwords) folded away (user request 2026-10-03). */}
      {view.connections && (
      <details class="card sms-connections">
        <summary>
          <b>{s().connectionsTitle}</b> <span class="muted">{s().connectionsState(view.sms.hasPassword, view.mail.hasPassword)}</span>
        </summary>
        <PhoneSettings view={view} onView={setView} onFlash={props.onFlash} />
        <MailSettings view={view} onView={setView} onFlash={props.onFlash} />
      </details>
      )}
    </>
  );
}

const configBody = (c: BingoSignupConfig) => {
  const { open, maxCodes, info, template, smsInstant, emailInstant, instantBatchId, emailSubject, emailTemplate, smsSignup, ipLock } = c;
  return { open, maxCodes, info, template, smsInstant, emailInstant, instantBatchId, emailSubject, emailTemplate, smsSignup, ipLock };
};

/** One switch of the settings, its explanation below it. */
function Switch(props: { checked: boolean; label: string; hint: string; disabled: boolean; onChange: (on: boolean) => void }) {
  return (
    <div class="signup-switch">
      <label class="switch">
        <input type="checkbox" checked={props.checked} disabled={props.disabled} onChange={(e) => props.onChange(e.currentTarget.checked)} />
        <span class="switch-track" aria-hidden="true" />
        {props.label}
      </label>
      <small class="muted">{props.hint}</small>
    </div>
  );
}

/**
 * All the settings of handing out the codes in one place, one under another (user request 2026-10-07):
 * switches and choices are saved at once, number and text fields when the field is left – no Save button.
 */
function FormSettings(props: { view: View; onView: (v: View) => void; onFlash: (f: Flash) => void }) {
  const [cfg, setCfg] = useState<BingoSignupConfig>(props.view.config);
  const [playerMax, setPlayerMax] = useState(props.view.playerMax);
  const [busy, setBusy] = useState(false);
  const count = props.view.signups.reduce((sum, x) => sum + x.coupons, 0);
  const saved = props.view.config;
  const save = async (change: Partial<BingoSignupConfig>, text: string = s().saved) => {
    const before = cfg;
    setCfg({ ...cfg, ...change });
    setBusy(true);
    try {
      props.onView(await api<View>('PUT', '/api/admin/bingo/signups/config', configBody({ ...saved, ...change })));
      props.onFlash({ ok: true, text });
    } catch (err) {
      setCfg(before);
      props.onFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  /**
   * A field is saved when it is left (onBlur: preact/compat turns a text field's onChange into onInput, which saved
   * the previous keystroke's value), if it changed and is valid (otherwise it goes back).
   */
  const leave = <K extends keyof BingoSignupConfig>(key: K, valid: boolean) => {
    if (cfg[key] === saved[key]) return;
    if (!valid) {
      setCfg({ ...cfg, [key]: saved[key] });
      props.onFlash({ ok: false, text: s().invalidField });
      return;
    }
    void save({ [key]: cfg[key] } as Partial<BingoSignupConfig>);
  };
  const saveMax = async () => {
    if (playerMax === props.view.playerMax) return;
    if (!Number.isInteger(playerMax) || playerMax < 1 || playerMax > BINGO_BATCH_MAX) {
      setPlayerMax(props.view.playerMax);
      props.onFlash({ ok: false, text: s().invalidField });
      return;
    }
    try {
      await api('PUT', '/api/admin/bingo/players/max', { max: playerMax });
      props.onView({ ...props.view, playerMax });
      props.onFlash({ ok: true, text: t.admin.bingoPlayers.maxSaved(playerMax) });
    } catch (err) {
      setPlayerMax(props.view.playerMax);
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };
  const template = (text: string) => text.trim().length >= 10 && text.includes('{koodi}') && !text.includes('{linkki}');
  const bot = saved.botCheck;
  return (
    <section class="card form">
      <h2>{s().formTitle}</h2>
      <p class="hint">{s().formInfo}</p>
      {bot === 'missing' && <p class="error">{s().botMissing}</p>}
      {bot === 'test' && <p class="error">{s().botTest}</p>}
      <div class="signup-switches">
        <Switch
          checked={cfg.open}
          label={s().open}
          hint={s().openHint}
          disabled={busy}
          onChange={(open) => void save({ open }, open ? s().formOpened : s().formClosed)}
        />
        <Switch
          checked={cfg.smsSignup}
          label={s().smsSignup}
          hint={s().smsSignupInfo}
          disabled={busy}
          onChange={(smsSignup) => void save({ smsSignup }, smsSignup ? s().smsSignupOn : s().smsSignupOff)}
        />
        {cfg.smsSignup && !props.view.sms.hasPassword && <p class="error">{s().smsInstantNoPhone}</p>}
        {cfg.smsSignup && props.view.smsInbox.error && <p class="error">{s().smsInboxError(props.view.smsInbox.error)}</p>}
        {cfg.smsSignup && !props.view.smsInbox.error && props.view.smsInbox.okAt !== null && (
          <p class="muted">{s().smsInboxOk(when(props.view.smsInbox.okAt))}</p>
        )}
        <Switch checked={cfg.ipLock} label={s().ipLock} hint={s().ipLockHint} disabled={busy} onChange={(ipLock) => void save({ ipLock })} />
        {/* At once or by hand, separately for text messages and e-mails (user request 2026-10-03). */}
        <Switch checked={cfg.smsInstant} label={s().smsInstant} hint={s().instantHint} disabled={busy} onChange={(smsInstant) => void save({ smsInstant })} />
        {cfg.smsInstant && !props.view.sms.hasPassword && <p class="error">{s().smsInstantNoPhone}</p>}
        <Switch
          checked={cfg.emailInstant}
          label={s().emailInstant}
          hint={s().instantHint}
          disabled={busy}
          onChange={(emailInstant) => void save({ emailInstant })}
        />
        {cfg.emailInstant && !props.view.mail.hasPassword && <p class="error">{s().emailOff}</p>}
      </div>
      <label>
        {s().instantBatch}
        <select
          value={cfg.instantBatchId === null ? '' : String(cfg.instantBatchId)}
          disabled={busy}
          onChange={(e) => void save({ instantBatchId: e.currentTarget.value ? Number(e.currentTarget.value) : null })}
        >
          <option value="">{s().instantBatchNone}</option>
          {props.view.batches.map((b) => (
            <option key={b.id} value={String(b.id)}>
              {s().emailBatchOption(b.label, b.free)}
            </option>
          ))}
        </select>
        <small>{s().instantBatchHint}</small>
      </label>
      {(cfg.smsInstant || cfg.emailInstant) && cfg.instantBatchId === null && <p class="error">{s().instantNoBatch}</p>}
      <label>
        {s().maxCodes}
        <input
          type="number"
          min={1}
          max={1000}
          value={cfg.maxCodes}
          onInput={(e) => setCfg({ ...cfg, maxCodes: Number(e.currentTarget.value) })}
          onBlur={() => leave('maxCodes', Number.isInteger(cfg.maxCodes) && cfg.maxCodes >= 1 && cfg.maxCodes <= 1000)}
        />
        <small>{s().count(count, saved.maxCodes)}</small>
      </label>
      {/* The general coupon limit per player (decisions 2026-10-01), moved here from the players tab. */}
      <label>
        {t.admin.bingoPlayers.max}
        <input
          type="number"
          min={1}
          max={BINGO_BATCH_MAX}
          value={playerMax}
          onInput={(e) => setPlayerMax(Number(e.currentTarget.value))}
          onBlur={() => void saveMax()}
        />
        <small>{s().playerMaxHint}</small>
      </label>
      <label>
        {s().info}
        <input
          value={cfg.info}
          maxLength={300}
          onInput={(e) => setCfg({ ...cfg, info: e.currentTarget.value })}
          onBlur={() => leave('info', true)}
          placeholder={s().infoPlaceholder}
        />
      </label>
      <CopyField value={`${PUBLIC_ORIGIN}/bingo/ilmoittaudu/`} label={s().formLink} />
      <details class="signup-texts">
        <summary>{s().textsTitle}</summary>
        <label>
          {s().template}
          <textarea
            rows={3}
            maxLength={300}
            value={cfg.template}
            onInput={(e) => setCfg({ ...cfg, template: e.currentTarget.value })}
            onBlur={() => leave('template', template(cfg.template))}
          />
          <small>{s().templateHint}</small>
        </label>
        <p class="hint">
          {s().preview}: <i>{messageText(cfg.template, ['ABCD2345', 'EFGH6789'], 'Pelaaja')}</i>
        </p>
        <label>
          {s().emailSubject}
          <input
            value={cfg.emailSubject}
            maxLength={150}
            onInput={(e) => setCfg({ ...cfg, emailSubject: e.currentTarget.value })}
            onBlur={() => leave('emailSubject', cfg.emailSubject.trim().length >= 3)}
          />
        </label>
        <label>
          {s().emailTemplate}
          <textarea
            rows={8}
            maxLength={2000}
            value={cfg.emailTemplate}
            onInput={(e) => setCfg({ ...cfg, emailTemplate: e.currentTarget.value })}
            onBlur={() => leave('emailTemplate', template(cfg.emailTemplate))}
          />
          <small>{s().emailTemplateHint}</small>
        </label>
      </details>
    </section>
  );
}

function statusBadge(x: BingoSignupView) {
  const label = s().status[x.status] ?? x.status;
  return <span class={`badge sms-${x.status}`}>{label}</span>;
}

function SignupList(props: { view: View; onView: (v: View) => void; onFlash: (f: Flash) => void }) {
  const list = props.view.signups;
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [label, setLabel] = useState('');
  const [plan, setPlan] = useState<Plan | null>(null);
  const [confirmClear, setConfirmClear] = useState<'selected' | 'all' | null>(null);
  const [busy, setBusy] = useState(false);
  const sendable = list.filter((x) => x.status === 'pending' || x.status === 'failed');
  const toggle = (id: number, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(id);
    else next.delete(id);
    setSelected(next);
  };
  const ids = [...selected].filter((id) => sendable.some((x) => x.id === id));
  const preview = async () => {
    if (!label.trim()) return props.onFlash({ ok: false, text: s().labelMissing });
    try {
      setPlan((await api<{ plan: Plan }>('POST', '/api/admin/bingo/signups/plan', { ids, label: label.trim() })).plan);
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };
  const send = async () => {
    setPlan(null);
    setBusy(true);
    try {
      props.onView(await api<View>('POST', '/api/admin/bingo/signups/send', { ids, label: label.trim() }));
      setSelected(new Set());
      props.onFlash({ ok: true, text: s().sending(ids.length) });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  const clear = async (which: 'selected' | 'all') => {
    setConfirmClear(null);
    try {
      props.onView(await api<View>('POST', '/api/admin/bingo/signups/delete', which === 'all' ? { all: true } : { ids: [...selected] }));
      setSelected(new Set());
      props.onFlash({ ok: true, text: s().removed });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };
  const job = props.view.job;
  const template = props.view.config.template;
  return (
    <section class="card form">
      <h2>{s().listTitle(list.length)}</h2>
      <p class="hint">{s().listInfo}</p>
      {job && <p class="success">{s().progress(job.done, job.total)}</p>}
      {list.length === 0 ? (
        <p class="muted">{s().none}</p>
      ) : (
        <>
          <div class="bingo-start-actions">
            <button class="btn btn-small" type="button" onClick={() => setSelected(new Set(sendable.map((x) => x.id)))} disabled={sendable.length === 0}>
              {s().selectAll(sendable.length)}
            </button>
            <button class="btn btn-small" type="button" onClick={() => setSelected(new Set())} disabled={selected.size === 0}>
              {s().selectNone}
            </button>
          </div>
          <div class="sms-table-wrap">
            <table class="sms-table">
              <thead>
                <tr>
                  <th />
                  <th>{s().colName}</th>
                  <th>{s().colPhone}</th>
                  <th>{s().colTime}</th>
                  <th>{s().colState}</th>
                  <th>{s().colCode}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((x) => (
                  <tr key={x.id}>
                    <td>
                      <input type="checkbox" checked={selected.has(x.id)} onChange={(e) => toggle(x.id, e.currentTarget.checked)} aria-label={x.name} />
                    </td>
                    <td>
                      {x.name}
                      <div class="muted">
                        {s().codesAsked(x.coupons)}
                        {x.via === 'sms' && ` · ${s().viaSms}`}
                      </div>
                    </td>
                    <td class="sms-contact">{x.email ? `✉️ ${x.email}` : `📱 ${formatPhone(x.phone ?? '')}`}</td>
                    <td>{when(x.createdAt)}</td>
                    <td>
                      {statusBadge(x)}
                      {x.error && <div class="sms-error">{x.error}</div>}
                    </td>
                    <td>
                      {x.codes.length > 0 ? (
                        <>
                          {x.codes.map((c) => (
                            <div key={c}>
                              <code>{spaced(c)}</code>
                            </div>
                          ))}
                          <div class="muted">{x.batchLabel ?? ''}</div>
                        </>
                      ) : (
                        '–'
                      )}
                    </td>
                    <td>
                      {x.codes.length > 0 && x.phone && (
                        <a
                          class="btn btn-small"
                          href={`https://wa.me/${x.phone.replace('+', '')}?text=${encodeURIComponent(messageText(template, x.codes, x.name))}`}
                          target="_blank"
                          rel="noopener"
                          title={s().whatsappTitle}
                        >
                          WhatsApp
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div class="grid-2">
            <label>
              {s().batchLabel}
              <input value={label} maxLength={BINGO_LABEL_MAX} onInput={(e) => setLabel(e.currentTarget.value)} placeholder={s().batchPlaceholder} />
            </label>
          </div>
          <div class="bingo-start-actions">
            <button class="btn btn-primary" type="button" disabled={busy || ids.length === 0 || Boolean(job)} onClick={() => void preview()}>
              {s().send(ids.length)}
            </button>
            <button class="btn btn-danger btn-small" type="button" disabled={selected.size === 0} onClick={() => setConfirmClear('selected')}>
              {s().removeSelected(selected.size)}
            </button>
            <button class="btn btn-danger btn-small" type="button" onClick={() => setConfirmClear('all')}>
              {s().removeAll}
            </button>
          </div>
        </>
      )}
      {plan && (
        <ConfirmDialog
          title={s().confirmTitle(plan.messages)}
          confirmLabel={`📱 ${s().confirmSend}`}
          cancelLabel={t.admin.cancel}
          confirmDisabled={plan.batch.exists && plan.batch.free < plan.needed}
          onConfirm={() => void send()}
          onCancel={() => setPlan(null)}
        >
          <p>{s().confirmText(plan.sms, plan.emails, plan.needed, label.trim())}</p>
          {plan.batch.exists ? (
            plan.batch.free < plan.needed ? (
              <p class="error">{s().notEnough(plan.batch.free, plan.needed)}</p>
            ) : (
              <p>{s().useExisting(plan.batch.free)}</p>
            )
          ) : (
            plan.needed > 0 && <p>{s().willCreate(plan.creates.join(' + '))}</p>
          )}
          {plan.batch.exists && !plan.batch.registrationOpen && <p class="error">{s().registrationClosed}</p>}
        </ConfirmDialog>
      )}
      {confirmClear && (
        <ConfirmDialog
          title={confirmClear === 'all' ? s().removeAllTitle : s().removeSelectedTitle(selected.size)}
          confirmLabel={t.admin.bingo.remove}
          cancelLabel={t.admin.cancel}
          danger
          onConfirm={() => void clear(confirmClear)}
          onCancel={() => setConfirmClear(null)}
        >
          <p>{s().removeText}</p>
        </ConfirmDialog>
      )}
    </section>
  );
}

function PhoneSettings(props: { view: View; onView: (v: View) => void; onFlash: (f: Flash) => void }) {
  const [url, setUrl] = useState(props.view.sms.url);
  const [username, setUsername] = useState(props.view.sms.username);
  const [password, setPassword] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    try {
      props.onView(await api<View>('PUT', '/api/admin/bingo/signups/sms', { url: url.trim(), username: username.trim(), password: password || null }));
      setPassword('');
      props.onFlash({ ok: true, text: s().phoneSaved });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  const test = async () => {
    if (!normalizePhone(testPhone)) return props.onFlash({ ok: false, text: t.errors.invalid_phone });
    setBusy(true);
    try {
      const r = await api<{ ok: boolean; error: string | null }>('POST', '/api/admin/bingo/signups/test', { phone: testPhone });
      props.onFlash(r.ok ? { ok: true, text: s().testSent } : { ok: false, text: r.error ?? s().testFailed });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <form class="card form" onSubmit={save}>
      <h2>{s().phoneTitle}</h2>
      <p class="hint">{s().phoneInfo}</p>
      <div class="grid-2">
        <label>
          {s().url}
          <input value={url} onInput={(e) => setUrl(e.currentTarget.value)} placeholder="http://192.168.1.50:8080" required />
        </label>
        <label>
          {s().username}
          <input value={username} onInput={(e) => setUsername(e.currentTarget.value)} autocomplete="off" required />
        </label>
        <label>
          {s().password}
          <input
            type="password"
            value={password}
            onInput={(e) => setPassword(e.currentTarget.value)}
            autocomplete="new-password"
            placeholder={props.view.sms.hasPassword ? s().passwordKept : ''}
            required={!props.view.sms.hasPassword}
          />
        </label>
      </div>
      <button class="btn btn-primary" type="submit" disabled={busy}>
        {s().savePhone}
      </button>
      <div class="grid-2">
        <label>
          {s().testPhone}
          <input value={testPhone} onInput={(e) => setTestPhone(e.currentTarget.value)} type="tel" placeholder="040 123 4567" />
        </label>
      </div>
      <button class="btn" type="button" disabled={busy || !props.view.sms.hasPassword} onClick={() => void test()}>
        📱 {s().test}
      </button>
    </form>
  );
}

function MailSettings(props: { view: View; onView: (v: View) => void; onFlash: (f: Flash) => void }) {
  const [host, setHost] = useState(props.view.mail.host);
  const [port, setPort] = useState(props.view.mail.port);
  const [username, setUsername] = useState(props.view.mail.username);
  const [password, setPassword] = useState('');
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    try {
      props.onView(await api<View>('PUT', '/api/admin/bingo/signups/mail', { host: host.trim(), port, username: username.trim(), password: password || null }));
      setPassword('');
      props.onFlash({ ok: true, text: s().mailSaved });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  const test = async () => {
    if (!normalizeEmail(testTo)) return props.onFlash({ ok: false, text: t.errors.invalid_email });
    setBusy(true);
    try {
      const r = await api<{ ok: boolean; error: string | null }>('POST', '/api/admin/bingo/signups/test-mail', { email: testTo });
      props.onFlash(r.ok ? { ok: true, text: s().mailTestSent } : { ok: false, text: r.error ?? s().testFailed });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <form class="card form" onSubmit={save}>
      <h2>{s().mailTitle}</h2>
      <p class="hint">{s().mailInfo}</p>
      <div class="grid-2">
        <label>
          {s().mailHost}
          <input value={host} onInput={(e) => setHost(e.currentTarget.value)} required />
        </label>
        <label>
          {s().mailPort}
          <input type="number" min={1} max={65535} value={port} onInput={(e) => setPort(Number(e.currentTarget.value))} required />
        </label>
        <label>
          {s().mailUser}
          <input value={username} onInput={(e) => setUsername(e.currentTarget.value)} autocomplete="off" required />
        </label>
        <label>
          {s().password}
          <input
            type="password"
            value={password}
            onInput={(e) => setPassword(e.currentTarget.value)}
            autocomplete="new-password"
            placeholder={props.view.mail.hasPassword ? s().passwordKept : ''}
            required={!props.view.mail.hasPassword}
          />
        </label>
      </div>
      <button class="btn btn-primary" type="submit" disabled={busy}>
        {s().saveMail}
      </button>
      <div class="grid-2">
        <label>
          {s().mailTestTo}
          <input value={testTo} onInput={(e) => setTestTo(e.currentTarget.value)} type="email" placeholder="nimi@esimerkki.fi" />
        </label>
      </div>
      <button class="btn" type="button" disabled={busy || !props.view.mail.hasPassword} onClick={() => void test()}>
        ✉️ {s().mailTest}
      </button>
    </form>
  );
}
